import { useEffect, useState, type JSX } from 'react';
import { SkipIntroContext, clearPrerenderedMarkupFlag, peekPrerenderedMarkupFlag } from './anim';
import { LandingAtmosphere } from './LandingAtmosphere';
import { LandingNav } from './LandingNav';
import { MobileCtaBar } from './MobileCtaBar';
import { HeroSection } from './HeroSection';
import { WhatIsSection } from './WhatIsSection';
import { ProofBand } from './ProofBand';
import { FeaturesSection } from './FeaturesSection';
import { HowItWorksSection } from './HowItWorksSection';
import { GallerySection } from './GallerySection';
import { VsZoomSection } from './VsZoomSection';
import { BelongingSection } from './BelongingSection';
import { FaqSection } from './FaqSection';
import { FinalCtaSection } from './FinalCtaSection';
import { LandingFooter } from './LandingFooter';

/**
 * The marketing landing page ("/"). The product's front door — shown to EVERYONE, signed-in or not.
 *
 *  • A NEW visitor sees the pitch; every primary CTA routes to /register (the frictionless guest creator).
 *  • A RETURNING, authed device ALSO sees the landing (no silent redirect away from `/`), but the CTAs
 *    flip to "Enter your office" → /office — one obvious tap back in. (The token still IS the identity;
 *    we just stopped hijacking `/`.) This page never duplicates workspace-creation logic — it only sells
 *    + sends you to /register or /office. Authed-awareness lives in {@link CreateRoomButton} + LandingNav.
 *
 * Scrolling: the app's global `body { overflow: hidden }` (the office is a fixed full-screen canvas) would
 * trap this long page. We opt THIS route into normal document scrolling by toggling `overflow` on body +
 * #root for the page's lifetime, and restore it on unmount so the office is unaffected. We also enable
 * smooth in-page anchor scrolling for the nav links while we're here.
 */
export function LandingPage(): JSX.Element {
  // SSG hand-off: when the SPA boots over the prerendered "/" markup (flag set by main.tsx), skip
  // the entrance reveals for THIS first mount — the visitor is already reading the static content;
  // blinking it out to re-animate would read as broken. The initializer only PEEKS (pure); the
  // one-shot clear happens in the mount effect below, because a concurrent render pass can be
  // discarded and re-run — a clearing initializer lost the flag on the retry (live prod bug).
  // Later navigations therefore animate normally.
  const [skipIntro] = useState(peekPrerenderedMarkupFlag);
  useEffect(() => {
    clearPrerenderedMarkupFlag();
  }, []);

  // Opt this route into document scrolling (the global office layout disables it). Restored on unmount.
  useEffect(() => {
    const root = document.getElementById('root');
    const prevBody = document.body.style.overflow;
    const prevRoot = root?.style.overflow;
    const prevHtmlScroll = document.documentElement.style.scrollBehavior;
    document.body.style.overflow = 'auto';
    if (root) root.style.overflow = 'visible';
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.body.style.overflow = prevBody;
      if (root) root.style.overflow = prevRoot ?? '';
      document.documentElement.style.scrollBehavior = prevHtmlScroll;
    };
  }, []);

  // Dev observability: the page is mounted + ready (the scene flips its own `sceneAlive`). Mirrors the
  // First-Breath flag so the Playwright agent can assert the landing rendered.
  useEffect(() => {
    if (typeof window !== 'undefined' && import.meta.env?.DEV) {
      const w = window as unknown as { __duckLanding?: { ready: boolean; sceneAlive: boolean } };
      w.__duckLanding = { ready: true, sceneAlive: w.__duckLanding?.sceneAlive ?? false };
    }
  }, []);

  return (
    <SkipIntroContext.Provider value={skipIntro}>
      <div data-testid="landing-root" className="relative min-h-screen w-full bg-[#0b1020] text-[#e6e8f0]">
        {/* Ambient light drifting behind EVERYTHING (fixed) — the layer that makes the page feel alive. */}
        <LandingAtmosphere />
        {/* Content rides above the atmosphere. */}
        <div className="relative z-10">
          <LandingNav />
          <main>
            <HeroSection />
            <WhatIsSection />
            <ProofBand />
            <FeaturesSection />
            <HowItWorksSection />
            <GallerySection />
            <VsZoomSection />
            <BelongingSection />
            <FaqSection />
            <FinalCtaSection />
          </main>
          <LandingFooter />
        </div>
        {/* Always-reachable primary CTA on phones — slides up once you scroll past the hero. */}
        <MobileCtaBar />
      </div>
    </SkipIntroContext.Provider>
  );
}
